import React from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../components/SEOMeta";

const TermsOfService: React.FC = () => {
  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Terms of Service",
    description: "Terms of Service for CyberAnnals - Learn about our policies, user responsibilities, and guidelines for using our cybersecurity archive website.",
    url: "https://cyberannals.com/terms-of-service",
    about: {
      "@type": "Organization",
      name: "CyberAnnals",
      description: "Comprehensive historical archive of cyber attacks dedicated to documenting and preserving the chronicles of cyber attacks throughout history."
    }
  };

  return (
    <>
      <SEOMeta
        title="Terms of Service - CyberAnnals | Cybersecurity Archive"
        description="Terms of Service for CyberAnnals - Learn about our policies, user responsibilities, and guidelines for using our educational cybersecurity archive website."
        keywords="terms of service, cyberannals terms, website policies, user agreement, cybersecurity website terms"
        canonical="https://cyberannals.com/terms-of-service"
        author="Muhammad Hussain"
        ogType="website"
        ogImage="https://cyberannals.com/cyberAnnals_logo.png"
        structuredData={structuredData}
      />

      {/* Hidden JSON-LD for structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-400 dark:to-blue-300 bg-clip-text text-transparent">
              Terms of Service
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-amber-500 mx-auto rounded-full"></div>
            <p className="text-lg text-slate-600 dark:text-slate-400 mt-6 max-w-2xl mx-auto">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          <div className="prose prose-lg dark:prose-invert max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Acceptance of Terms
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                By accessing and using CyberAnnals (the "Service"), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to abide by these Terms of Service, you are not authorized to use or access the Service.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Description of Service
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                CyberAnnals provides a comprehensive historical archive of cyber attacks, cybersecurity events, and related educational content. The website is provided for educational and informational purposes only. All content is for general information and educational purposes only.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Use License
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                You may access and view the educational content on CyberAnnals for personal, educational, and research purposes. You may share links to our content, and reference our educational materials with appropriate attribution. The content is provided for educational purposes and should not be used for commercial purposes without our consent.
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>You may not reproduce large portions of our content without attribution</li>
                <li>You may not use our content for commercial purposes without permission</li>
                <li>You may not attempt to decompile or reverse engineer any software elements of our site</li>
                <li>You should preserve copyright notices when sharing our content</li>
                <li>You may not systematically scrape or download our entire content database</li>
              </ul>
              <p className="text-lg leading-relaxed">
                This agreement remains in effect as long as you access our educational content.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Disclaimer
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                The cybersecurity information, historical event data, and educational content on CyberAnnals are provided for educational and informational purposes. We make every effort to keep the information accurate and up-to-date.
              </p>
              <p className="text-lg leading-relaxed">
                We take responsibility for the accuracy of our content and encourage users to verify critical information. While we strive to provide reliable information, users should consider their specific circumstances when applying cybersecurity recommendations.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Limitations
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                While we strive to provide reliable information, CyberAnnals is not liable for any indirect, incidental, or consequential damages resulting from the use of our educational content.
              </p>
            </section>

            {/* <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Intellectual Property
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                The intellectual property rights for content created by CyberAnnals, including our unique compilation, organization, and presentation of cybersecurity information, are owned by CyberAnnals. We respect the intellectual property rights of original sources and provide proper attribution where required.
              </p>
              <p className="text-lg leading-relaxed">
                Our content, including our unique compilation of historical cybersecurity events, educational articles, and website design, is protected by copyright. We provide this content for educational purposes and proper attribution is expected when referencing our work.
              </p>
            </section> */}

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                User Communications
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                Communications sent through our contact form are used solely to respond to your inquiries and improve our educational content. We respect your privacy and do not use your communications for any other purposes without your consent.
              </p>
              <p className="text-lg leading-relaxed">
                You retain ownership of any original content you share with us, and we will respect your rights to that content.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Accuracy of Materials
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                We strive to ensure all materials on CyberAnnals are accurate, complete, and current. We make efforts to correct any errors promptly when identified. CyberAnnals is committed to maintaining the quality and accuracy of our educational content and updates materials as needed to reflect current cybersecurity knowledge.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Links
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                CyberAnnals reviews and verifies the linked sites, particularly our affiliate and partner links. We take responsibility for the quality and relevance of the external resources we recommend. While we make efforts to ensure linked sites are reputable, users should exercise their own judgment when visiting external sites.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Modifications
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                CyberAnnals may revise these terms of service at any time with appropriate notice. We will update the "Last Updated" date at the top of this page when changes are made. By continuing to use this website, you agree to be bound by the then-current version of these terms of service.
              </p>
            </section>

            {/* <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Governing Law
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                These terms shall be governed by and construed in accordance with the laws of the jurisdiction where CyberAnnals operates, without regard to its conflict of law provisions.
              </p>
            </section> */}

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Contact Us
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                If you have any questions about these Terms of Service, please contact us through our <Link to="/contact" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline">Contact Us</Link> page.
              </p>
            </section>
          </div>

          {/* Navigation Links */}
          <div className="flex flex-wrap justify-center gap-4 pt-8 border-t border-slate-200 dark:border-slate-700 mt-12">
            <Link
              to="/privacy-policy"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm"
            >
              Privacy Policy
            </Link>
            <Link
              to="/about"
              className="px-6 py-3 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition-colors font-medium text-sm"
            >
              About Us
            </Link>
            <Link
              to="/"
              className="px-6 py-3 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition-colors font-medium text-sm"
            >
              Home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;